import { useState, useEffect } from "react";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import FilterIcon from "../../shared-components/icons/FilterIcon";
import { ReportSummaryItemApi } from "../../api/Reports";
import useNavigateToSummaryWithState from "../../reports/useNavigateToSummaryWithState";
import {
  ReportAdminCancelButton,
  ReportChangeDueDateAdmindButton,
  ReportLogAdminCompletedButton,
} from "../admin-completed/TableAdminCompletedButtons";
import ReportStepsModal from "../../reports/ReportStepsModal";
import ReportStatus from "../../reports/ReportStatus";
import { reportFailed } from "../../reports/utils";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import CopyIcon from "../../shared-components/CopyIcon";
import TableReportDateWithFormat from "../../shared-components/TableReportDateWithFormat";

interface TableProps {
  data: ReportSummaryItemApi[] | undefined;
  header: Array<{ name: string; sortController?: string }>;
  isLoading: boolean;
  hasFilters: (value: string) => boolean;
  openFilter: (value: string) => void;
  hasError: boolean;
  onChange: () => void;
}

const Table = ({
  data,
  header,
  isLoading,
  openFilter,
  hasFilters,
  hasError,
  onChange,
}: TableProps) => {
  const [tableData, setTableData] = useState<ReportSummaryItemApi[]>([]);
  const { navigateToSummary } = useNavigateToSummaryWithState();

  useEffect(() => {
    if (data) setTableData(data);
  }, [data]);

  function handleReportClick(report_uuid: string) {
    navigateToSummary(report_uuid);
  }

  return (
    <>
      <div>
        <div className="flex text-gray-500 rounded-t-xl border shadow shadow-gray-300">
          {header.map((header) => (
            <div
              className="flex rounded-t-xl items-center p-3 text-xs leading-4 font-medium bg-gray-50 uppercase flex-1"
              key={header.name}
            >
              {header.name}
              {header.sortController && (
                <FilterIcon
                  width={16}
                  className={`my-div ${
                    hasFilters(header.sortController) ? "fill-ourGreen" : ""
                  }`}
                  onClick={() => openFilter(header.sortController!)}
                ></FilterIcon>
              )}
            </div>
          ))}
        </div>
        <div className="bg-white border-l border-r rounded-b-xl shadow shadow-gray-300">
          {isLoading ? <LoadingSpinner /> : ""}
          {tableData.length === 0 && !isLoading ? (
            <>
              {!hasError && (
                <div className="flex justify-center p-4 ">No Found Reports</div>
              )}
              {hasError && (
                <div className="flex flex-row items-center justify-center py-4">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                  <div className="text-red-500">Couldn't get data</div>
                </div>
              )}
            </>
          ) : (
            tableData.map((report, i) => (
              <div key={`completed-${i}`}>
                <div className="flex border-b text-xs leading-4 font-normal py-4 hover:bg-gray-50 text-gray-700">
                  {/* NAME / COMPANY / ID */}
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div>
                      <div className="font-semibold underline cursor-pointer pb-1">
                        <span
                          onClick={() => handleReportClick(report.report_uuid)}
                          className="underline cursor-pointer font-semibold"
                        >
                          {report.full_name}
                        </span>
                      </div>
                      <div className="pb-1">{report.company_name}</div>
                      <div className="flex items-center gap-1">
                        <CopyIcon textToCopy={report.report_uuid} />
                        {report.report_uuid}
                      </div>
                    </div>
                  </div>
                  {/* PARENT COMPANY */}
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div>{report.parent_company_name}</div>
                  </div>
                  {/* STATUS */}
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <>
                      <ReportStatus
                        stepStatusName={report.step_name}
                        failed={reportFailed(report)}
                        report={report}
                        allowResumeOnClick={true}
                      />
                      <ReportStepsModal
                        workflowRunUUID={report.workflow_run_uuid}
                      />
                    </>
                  </div>
                  {/* CHECK TYPE / BEHAVIOR KIT */}
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div>
                      <div className="font-semibold underline  mb-2">
                        {report.blueprint_name}
                      </div>
                      <div>{report.behavior_kit_name}</div>
                    </div>
                  </div>

                  {/* DATE ADDED */}
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32 whitespace-pre">
                    <TableReportDateWithFormat
                      date={report.created_at}
                      updated={false}
                    />
                  </div>

                  {/* DUE DATE */}
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32 whitespace-pre">
                    <TableReportDateWithFormat
                      date={report.due_date}
                      updated={report.due_date_updated}
                    />
                  </div>

                  {/* CLIENT REF ID */}
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div>{report.client_report_id}</div>
                  </div>

                  {/* ACTIONS */}
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div className="flex gap-1">
                      <ReportChangeDueDateAdmindButton
                        report={report}
                        onChange={() => onChange()}
                      />
                      <ReportLogAdminCompletedButton
                        reportUUID={report.report_uuid}
                      />
                    </div>
                  </div>
                  <div className="flex px-3 flex-1  items-center truncate text-ellipsis w-32">
                    <div>
                      <ReportAdminCancelButton
                        reportUUID={report.report_uuid}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default Table;
